import React from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, ResponsiveContainer } from "recharts";
import { View, Text } from "../base";
import { COLORS } from "../../utils/chartData";
import { useTheme } from "../../contexts/ThemeContext";

export type TrainingMetricsHistoryEntry = {
  step: number;
  epoch: number;
  loss?: number; // train loss — present on training-log entries
  eval_loss?: number; // validation loss — present on per-epoch eval entries
  learning_rate?: number;
  grad_norm?: number;
};

export type TrainingMetrics = {
  history: TrainingMetricsHistoryEntry[];
  best?: { eval_loss: number; checkpoint: string; epoch: number };
  source?: string;
};

interface TrainingMetricsChartProps {
  metrics?: TrainingMetrics;
}

const TRAIN_COLOR = COLORS[0]; // blue
const EVAL_COLOR = COLORS[3]; // red

const fmtLoss = (v: number) => v.toFixed(3);
const fmtPpl = (v: number) => Math.exp(v).toFixed(2);

// The tooltip surfaces the raw loss values plus their derived perplexity (ppl = e^loss),
// since the control plane never sends a ppl field.
const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <View className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg">
      <Text className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">epoch {typeof label === "number" ? label.toFixed(2) : label}</Text>
      {payload
        .filter((entry: any) => entry.value != null)
        .map((entry: any, index: number) => (
          <View
            key={index}
            className="flex flex-row items-center gap-2"
          >
            <View
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <Text className="text-xs font-medium text-gray-900 dark:text-white">
              {entry.name}: {fmtLoss(entry.value)} (ppl {fmtPpl(entry.value)})
            </Text>
          </View>
        ))}
    </View>
  );
};

export const TrainingMetricsChart: React.FC<TrainingMetricsChartProps> = ({ metrics }) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const history = metrics?.history ?? [];
  if (history.length === 0) {
    return <Text className="text-sm text-gray-400">No metrics available.</Text>;
  }

  const gridColor = isDark ? "#374151" : "#E5E7EB";
  const textColor = isDark ? "#9CA3AF" : "#6B7280";

  // Train-loss and eval-loss entries are separate rows logged at different frequencies; sort by
  // step so the line paths follow training order, and let connectNulls bridge the gaps each series
  // leaves where the other series' rows sit.
  const data = [...history].sort((a, b) => a.step - b.step);
  const maxEpoch = Math.max(...history.map((h) => h.epoch), 0);
  // Headroom above the tallest point so the first-epoch loss isn't flush against the top edge and
  // the "best" reference-line label has room (round up to a clean integer tick with ~10% padding).
  const yMax = Math.max(...history.map((h) => Math.max(h.loss ?? 0, h.eval_loss ?? 0)), 0.1);
  const yDomainMax = Math.max(1, Math.ceil(yMax * 1.1));

  const best = metrics?.best;
  const bestCkpt = best?.checkpoint ? best.checkpoint.split("/").pop() : undefined;

  return (
    <ResponsiveContainer
      width="100%"
      height={260}
    >
      <LineChart
        data={data}
        margin={{ top: 28, right: 16, left: 0, bottom: 5 }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={gridColor}
        />
        <XAxis
          dataKey="epoch"
          type="number"
          domain={[0, maxEpoch]}
          allowDecimals
          tick={{ fill: textColor, fontSize: 10 }}
          tickLine={{ stroke: gridColor }}
          axisLine={{ stroke: gridColor }}
          tickFormatter={(v: number) => (Number.isInteger(v) ? String(v) : v.toFixed(1))}
        />
        <YAxis
          domain={[0, yDomainMax]}
          tick={{ fill: textColor, fontSize: 10 }}
          tickLine={{ stroke: gridColor }}
          axisLine={{ stroke: gridColor }}
          tickFormatter={(v: number) => v.toFixed(2)}
          width={46}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {best != null && (
          <ReferenceLine
            x={best.epoch}
            stroke={EVAL_COLOR}
            strokeDasharray="4 4"
            label={{
              value: bestCkpt ? `best (${bestCkpt})` : "best",
              position: "top",
              fill: textColor,
              fontSize: 10,
            }}
          />
        )}
        <Line
          type="monotone"
          dataKey="loss"
          name="train loss"
          stroke={TRAIN_COLOR}
          strokeWidth={2}
          dot={false}
          connectNulls
        />
        <Line
          type="monotone"
          dataKey="eval_loss"
          name="eval loss"
          stroke={EVAL_COLOR}
          strokeWidth={2}
          strokeDasharray="5 4"
          dot={{ fill: EVAL_COLOR, r: 3 }}
          activeDot={{ r: 5 }}
          connectNulls
        />
      </LineChart>
    </ResponsiveContainer>
  );
};
